import { formatINR } from '../../../shared/utils/formatters';
import React from 'react'
import { Table, Button, Tag, Space, Tooltip } from 'antd'
import { EyeOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons'
import type { ColumnsType } from 'antd/es/table'
import type { Commission } from '../types/commission.types'
import { SellerNameDisplay } from './SellerNameDisplay'

interface CommissionsTableProps {
    commissions: Commission[]
    onRowClick: (commission: Commission) => void
    onEdit: (commission: Commission) => void
    onDelete: (commission: Commission) => void
}

// Helper component for scope badges
const ScopeBadge: React.FC<{ scope: string }> = ({ scope }) => {
    const scopeColors: Record<string, string> = {
        product: 'blue',
        category: 'purple',
        seller: 'orange',
        campaign: 'red',
        global: 'default',
    }

    const scopeLabels: Record<string, string> = {
        product: 'Product',
        category: 'Category',
        seller: 'Seller',
        campaign: 'Campaign',
        global: 'Global',
    }

    return (
        <Tag color={scopeColors[scope] || 'default'}>
            {scopeLabels[scope] || scope}
        </Tag>
    )
}

// Helper component to display target based on scope
const TargetDisplay: React.FC<{ record: any }> = ({ record }) => {
    const scope = record.scope || 'product' // Default to product for old records

    switch (scope) {
        case 'product':
            if (record.products && Array.isArray(record.products) && record.products.length > 0) {
                const productNames = record.products.map((p: any) => p.name).join(', ')
                if (productNames.length > 40) {
                    return (
                        <Tooltip title={productNames}>
                            {productNames.substring(0, 40)}... ({record.products.length})
                        </Tooltip>
                    )
                }
                return <span>{productNames}</span>
            }
            return <Tag color="gray">No products</Tag>

        case 'category':
            return record.category?.name ? (
                <span>{record.category.name}</span>
            ) : (
                <Tag color="gray">No category</Tag>
            )

        case 'seller':
            return <SellerNameDisplay sellerId={record.seller_id || record.sellerId} />


        case 'campaign':
            return record.campaign?.name ? (
                <span>{record.campaign.name}</span>
            ) : (
                <span>Campaign #{record.campaign_id}</span>
            )

        case 'global':
            return <Tag color="cyan">All Orders</Tag>

        default:
            return <span>-</span>
    }
}

export const CommissionsTable: React.FC<CommissionsTableProps> = ({
    commissions,
    onRowClick,
    onEdit,
    onDelete
}) => {
    const columns: ColumnsType<Commission> = [

        {
            title: 'Scope',
            dataIndex: 'scope',
            key: 'scope',
            width: 120,
            render: (scope: string) => <ScopeBadge scope={scope || 'product'} />,
        },
        {
            title: 'Target',
            key: 'target',
            width: 200,
            render: (_, record: any) => <TargetDisplay record={record} />,
        },
        {
            title: 'Commission',
            key: 'commission',
            width: 150,
            render: (_, record: any) => {
                const type = record.commission_type || record.commissionType
                const value = record.commission_value ?? record.commissionValue

                if (value == null) return 'N/A'

                return (
                    <Space direction="vertical" size={0}>
                        <strong style={{ color: '#1890ff' }}>
                            {type === 'percentage' ? `${value}%` : formatINR(value.toLocaleString())}
                        </strong>
                        <Tag color={type === 'percentage' ? 'green' : 'orange'} style={{ fontSize: '11px' }}>
                            {type === 'percentage' ? 'Percentage' : 'Fixed'}
                        </Tag>
                    </Space>
                )
            },
        },
        {
            title: 'Priority',
            dataIndex: 'priority',
            key: 'priority',
            width: 90,
            render: (priority: number) => (
                <Tag color={priority <= 3 ? 'red' : priority <= 6 ? 'orange' : 'default'}>
                    {priority || 5}
                </Tag>
            ),
        },
        {
            title: 'Conditions',
            key: 'conditions',
            width: 180,
            render: (_, record: any) => {
                const conditions: string[] = []

                if (record.min_order_value != null || record.minOrderValue != null) {
                    const min = record.min_order_value ?? record.minOrderValue
                    conditions.push(`Min: ₹${min.toLocaleString()}`)
                }
                if (record.max_order_value != null || record.maxOrderValue != null) {
                    const max = record.max_order_value ?? record.maxOrderValue
                    conditions.push(`Max: ₹${max.toLocaleString()}`)
                }
                if (record.min_quantity != null || record.minQuantity != null) {
                    const min = record.min_quantity ?? record.minQuantity
                    conditions.push(`Min Qty: ${min}`)
                }
                if (record.max_quantity != null || record.maxQuantity != null) {
                    const max = record.max_quantity ?? record.maxQuantity
                    conditions.push(`Max Qty: ${max}`)
                }

                if (conditions.length === 0) {
                    return <Tag color="default">No limits</Tag>
                }

                return (
                    <Space direction="vertical" size={2}>
                        {conditions.map((cond, idx) => (
                            <span key={idx} style={{ fontSize: '12px' }}>{cond}</span>
                        ))}
                    </Space>
                )
            },
        },
        {
            title: 'Effective Period',
            key: 'effectivePeriod',
            width: 180,
            render: (_, record: any) => {
                const from = record.effective_from || record.effectiveFrom
                const to = record.effective_to || record.effectiveTo

                if (!from && !to) {
                    return <Tag color="green">Always Active</Tag>
                }

                const formatDate = (date: string) => {
                    return new Date(date).toLocaleDateString('en-IN', {
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric'
                    })
                }

                return (
                    <Space direction="vertical" size={2}>
                        {from && <span style={{ fontSize: '12px' }}>From: {formatDate(from)}</span>}
                        {to && <span style={{ fontSize: '12px' }}>To: {formatDate(to)}</span>}
                    </Space>
                )
            },
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            width: 100,
            render: (status: string) => (
                <Tag color={status === 'active' ? 'success' : 'default'}>
                    {status === 'active' ? 'Active' : 'Inactive'}
                </Tag>
            ),
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 150,
            fixed: 'right',
            render: (_, record) => (
                <Space>
                    <Button
                        type="text"
                        icon={<EyeOutlined />}
                        onClick={(e) => {
                            e.stopPropagation()
                            onRowClick(record)
                        }}
                    />
                    <Button
                        type="text"
                        icon={<EditOutlined />}
                        onClick={(e) => {
                            e.stopPropagation()
                            onEdit(record)
                        }}
                    />
                    <Button
                        type="text"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={(e) => {
                            e.stopPropagation()
                            onDelete(record)
                        }}
                    />
                </Space>
            ),
        },
    ]

    return (
        <Table
            columns={columns}
            dataSource={commissions}
            rowKey="id"
            scroll={{ x: 1400 }}
            pagination={{
                pageSize: 10,
                showSizeChanger: true,
                showTotal: (total) => `Total ${total} commissions`,
            }}
        />
    )
}
